import { CalendarClock, CalendarDays, Clock, MapPin, Pencil, UserRound } from "lucide-react";
import * as React from "react";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tooltip } from "@/components/ui/misc";
import type { DrawerMode } from "@/features/appointment-drawer";
import { isClosed, locationText, SOURCE_LABELS, statusInfo } from "@/lib/appointments";
import { appointmentMinutes, appointmentWhen } from "@/lib/contact-activity";
import type { AppointmentListItem } from "@/lib/types";
import { cn, formatDateTime } from "@/lib/utils";

/**
 * One appointment on the opportunity: when, where, with whom, how it stands — and the two things
 * ACL-411 asks for, Edit and Reschedule, which open the shared drawer over the dialog.
 *
 * This is the read-only card from `contact-appointments.tsx` with the controls the ticket needs.
 * It is a copy rather than a prop on that one, and that is a deliberate call worth stating: the
 * contact page's card is a *record* of what was booked and deliberately sends people to Calendars
 * to change it, while this one is a control surface. Threading an `onOpen` through the first would
 * leave two cards whose difference is a nullable callback, and the next person to touch either has
 * to work out which surface they are changing. If a third place needs the same card, that is the
 * moment to extract one — with the reason for the split in hand.
 *
 * **A closed appointment offers neither control.** Cancelled and rescheduled ones change no more
 * (`isClosed`), so the buttons are disabled with the reason rather than hidden: a row that silently
 * loses its controls reads as a bug, and a rescheduled one still links to the time that replaced it.
 */
export function AppointmentRow({
  appointment: a, phone, onOpen,
}: {
  appointment: AppointmentListItem;
  phone: string;
  onOpen: (id: number, mode: DrawerMode) => void;
}) {
  const status = statusInfo(a.status);
  const start = new Date(a.starts_at);
  const off = isClosed(a.status);
  const location = locationText(a.location_kind, a.location_detail, phone);
  const why = a.status === "cancelled" ? "This appointment was cancelled" : "This appointment was rescheduled";

  return (
    <article className={cn("rounded-lg border border-border p-3", off && "opacity-70")}>
      <div className="flex gap-3">
        <div className="flex w-12 shrink-0 flex-col items-center rounded-md bg-surface-sunken py-1.5 text-center" aria-hidden>
          <span className="text-2xs font-medium uppercase text-text-muted">
            {start.toLocaleDateString(undefined, { month: "short" })}
          </span>
          <span className="text-lg font-semibold leading-tight text-text">{start.getDate()}</span>
        </div>
        <div className="min-w-0 flex-1 space-y-1.5">
          <div className="flex flex-wrap items-center gap-2">
            <h5 className={cn("text-sm font-semibold text-text", off && "line-through")}>{a.title}</h5>
            <Badge tone="outline" size="sm">{SOURCE_LABELS[a.source] ?? a.source}</Badge>
            <Badge tone={status.tone} size="sm" className="ml-auto">{status.label}</Badge>
          </div>
          <dl className="grid gap-x-4 gap-y-1 text-xs text-text-muted sm:grid-cols-2">
            <Fact icon={Clock} label="When">
              {appointmentWhen(a)} <span className="text-text-subtle">({appointmentMinutes(a)} min)</span>
            </Fact>
            <Fact icon={CalendarDays} label="Calendar">{a.calendar_name || "—"}</Fact>
            <Fact icon={UserRound} label="Assigned to">{a.owner_name ?? "Unassigned"}</Fact>
            <Fact icon={MapPin} label="Location">
              {/^https?:\/\//i.test(location) ? (
                <a href={location} target="_blank" rel="noreferrer" className="break-all text-accent-11 hover:underline">
                  {location}
                </a>
              ) : (
                location
              )}
            </Fact>
          </dl>
          {a.notes && (
            <p className="whitespace-pre-wrap rounded-md bg-surface-sunken px-2.5 py-1.5 text-xs text-text">{a.notes}</p>
          )}
        </div>
      </div>

      <div className="mt-2.5 flex flex-wrap items-center gap-x-3 gap-y-1.5 border-t border-border pt-2.5">
        <Action
          icon={Pencil}
          label="Edit"
          disabled={off}
          why={why}
          onClick={() => onOpen(a.id, "edit")}
          describes={a.title}
        />
        <Action
          icon={CalendarClock}
          label="Reschedule"
          disabled={off}
          why={why}
          onClick={() => onOpen(a.id, "reschedule")}
          describes={a.title}
        />
        <p className="ml-auto text-2xs text-text-subtle">
          Booked {formatDateTime(a.created_at)}
          {" · "}
          <Link to={`/calendars?tab=list&appointment=${a.id}`} className="font-medium text-accent-11 hover:underline">
            Open in Calendars
          </Link>
        </p>
      </div>
    </article>
  );
}

/** A link-style control, or the same control off with the reason it cannot be used. */
function Action({
  icon: Icon, label, disabled, why, onClick, describes,
}: {
  icon: typeof Pencil;
  label: string;
  disabled: boolean;
  why: string;
  onClick: () => void;
  /** The appointment's title, so each row's control reads as its own to a screen reader. */
  describes: string;
}) {
  const button = (
    <Button
      variant="ghost"
      size="sm"
      className="h-auto gap-1 px-1.5 py-1 text-xs"
      disabled={disabled}
      onClick={onClick}
      // The reason rides on the control, not only in the tooltip: a Radix tooltip mounts its
      // words on hover, so a keyboard or screen-reader user reaching a disabled control this way
      // would otherwise be told it cannot be used and never why.
      aria-label={disabled ? `${label} "${describes}" — ${why}` : `${label} "${describes}"`}
      title={disabled ? why : undefined}
    >
      <Icon className="size-3.5" aria-hidden />
      {label}
    </Button>
  );
  // A disabled button fires no pointer events, so the tooltip needs a wrapper that can take focus
  // — the same shape `conversations/contact-panel.tsx` uses for its withheld actions.
  return disabled ? (
    <Tooltip content={why}>
      <span tabIndex={0} className="inline-block">{button}</span>
    </Tooltip>
  ) : (
    button
  );
}

function Fact({ icon: Icon, label, children }: { icon: typeof Clock; label: string; children: React.ReactNode }) {
  return (
    <div className="flex min-w-0 items-start gap-1.5">
      <Icon className="mt-0.5 size-3.5 shrink-0" aria-hidden />
      <dt className="sr-only">{label}</dt>
      <dd className="min-w-0 text-text">{children}</dd>
    </div>
  );
}
